/**
 * PAPPO — ReportsPanel (console /staff)
 * Fila de denúncias + detalhe com evidências (prints) e ações de triagem:
 * atribuir a si (em_analise) e encerrar (resolvida/descartada).
 */
import { useCallback, useEffect, useState } from 'react';
import { Flag, X, UserCheck, Check, Ban, Image as ImageIcon } from 'lucide-react';
import { Avatar } from '../common/Avatar';
import { PunishPanel } from './PunishPanel';
import {
  listReports,
  getReport,
  assignReport,
  resolveReport,
  REASON_LABEL,
  STATUS_LABEL,
  type ReportListItem,
  type ReportDetail,
  type ReportStatus,
} from '../../services/reports';
import styles from './ReportsPanel.module.css';

const FILTERS: { value?: ReportStatus; label: string }[] = [
  { value: undefined, label: 'Todas' },
  { value: 'aberta', label: 'Abertas' },
  { value: 'em_analise', label: 'Em análise' },
  { value: 'resolvida', label: 'Resolvidas' },
  { value: 'descartada', label: 'Descartadas' },
];

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ReportsPanel() {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [filter, setFilter] = useState<ReportStatus | undefined>(undefined);
  const [selected, setSelected] = useState<ReportDetail | null>(null);
  const [resolution, setResolution] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await listReports(filter);
    if (res.success && res.data) setReports(res.data.reports);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = useCallback(async (id: number) => {
    const res = await getReport(id);
    if (res.success && res.data) {
      setSelected(res.data.report);
      setResolution('');
    }
  }, []);

  const refresh = useCallback(
    async (id: number) => {
      const res = await getReport(id);
      if (res.success && res.data) setSelected(res.data.report);
      await load();
    },
    [load]
  );

  const handleAssign = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    await assignReport(selected.id);
    setBusy(false);
    await refresh(selected.id);
  }, [selected, refresh]);

  const handleResolve = useCallback(
    async (status: 'resolvida' | 'descartada') => {
      if (!selected) return;
      setBusy(true);
      await resolveReport(selected.id, status, resolution.trim());
      setBusy(false);
      setSelected(null);
      await load();
    },
    [selected, resolution, load]
  );

  const openCount = reports.filter((r) => r.status === 'aberta').length;

  return (
    <div>
      <div className={styles.filters}>
        {FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            className={`${styles.filterBtn} ${filter === f.value ? styles.filterActive : ''}`}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
        {openCount > 0 && <span className={styles.openBadge}>{openCount} aberta(s)</span>}
      </div>

      {reports.length === 0 ? (
        <div className={styles.empty}>
          <Flag size={28} />
          <p>Nenhuma denúncia {filter ? STATUS_LABEL[filter].toLowerCase() : ''}.</p>
        </div>
      ) : (
        <ul className={styles.list}>
          {reports.map((r) => (
            <li key={r.id}>
              <button className={styles.row} onClick={() => openDetail(r.id)} type="button">
                <Avatar name={r.reported_name} src={r.reported_avatar} size="md" />
                <div className={styles.rowInfo}>
                  <span className={styles.rowName}>{r.reported_name}</span>
                  <span className={styles.rowSub}>
                    por {r.reporter_name} · {fmtDate(r.created_at)}
                  </span>
                </div>
                <span className={`${styles.reasonBadge}`}>{REASON_LABEL[r.reason]}</span>
                {r.evidence_count > 0 && (
                  <span className={styles.evidenceCount}>
                    <ImageIcon size={13} /> {r.evidence_count}
                  </span>
                )}
                <span className={`${styles.statusBadge} ${styles[r.status]}`}>
                  {STATUS_LABEL[r.status]}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Detalhe */}
      {selected && (
        <div className={styles.overlay} onClick={() => setSelected(null)} role="dialog" aria-modal="true">
          <div className={styles.detail} onClick={(e) => e.stopPropagation()}>
            <div className={styles.detailHeader}>
              <h3>Denúncia #{selected.id}</h3>
              <span className={`${styles.statusBadge} ${styles[selected.status]}`}>
                {STATUS_LABEL[selected.status]}
              </span>
              <button className={styles.close} onClick={() => setSelected(null)} aria-label="Fechar">
                <X size={18} />
              </button>
            </div>

            <div className={styles.detailBody}>
              <div className={styles.reportedRow}>
                <Avatar name={selected.reported_name} src={selected.reported_avatar} size="md" />
                <div>
                  <div className={styles.rowName}>{selected.reported_name}</div>
                  <div className={styles.rowSub}>
                    Motivo: <strong>{REASON_LABEL[selected.reason]}</strong>
                  </div>
                </div>
              </div>

              <div className={styles.meta}>
                Denunciado por <strong>{selected.reporter_name}</strong> em{' '}
                {fmtDate(selected.created_at)}
                {selected.assigned_name && <> · analista: {selected.assigned_name}</>}
              </div>

              {selected.description && <p className={styles.desc}>{selected.description}</p>}

              {selected.evidence.length > 0 && (
                <>
                  <div className={styles.label}>Evidências</div>
                  <div className={styles.gallery}>
                    {selected.evidence.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={styles.evThumb}>
                        <img src={url} alt={`Evidência ${i + 1}`} />
                      </a>
                    ))}
                  </div>
                </>
              )}

              {selected.status === 'resolvida' || selected.status === 'descartada' ? (
                selected.resolution && (
                  <div className={styles.resolutionBox}>
                    <div className={styles.label}>
                      Encerrada por {selected.resolved_name}
                    </div>
                    <p className={styles.desc}>{selected.resolution}</p>
                  </div>
                )
              ) : (
                <>
                  {selected.status === 'aberta' && (
                    <button className={styles.assignBtn} onClick={handleAssign} disabled={busy}>
                      <UserCheck size={16} /> Atribuir a mim
                    </button>
                  )}
                  <div className={styles.label}>Resolução (nota da equipe)</div>
                  <textarea
                    className={styles.resInput}
                    rows={2}
                    placeholder="O que foi decidido / feito."
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                  />
                  <div className={styles.detailActions}>
                    <button
                      className={styles.dismiss}
                      onClick={() => handleResolve('descartada')}
                      disabled={busy}
                    >
                      <Ban size={16} /> Descartar
                    </button>
                    <button
                      className={styles.resolve}
                      onClick={() => handleResolve('resolvida')}
                      disabled={busy}
                    >
                      <Check size={16} /> Resolver
                    </button>
                  </div>
                </>
              )}

              {/* Ação de moderação (F3): punir o adulto denunciado */}
              <PunishPanel
                targetId={selected.reported_id}
                targetName={selected.reported_name}
                reportId={selected.id}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
